import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  Divider,
} from "@mui/material";
import { EVENT_TYPES, EVENT_PRIORITY } from "../types/eventTypes";

const EventForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    eventDetails: {
      eventName: "",
      eventType: "wedding",
      description: "",
      bookingDate: "",
      location: {
        venueName: "",
        address: "",
        area: "",
        city: "",
        district: "",
        state: "",
        pincode: "",
        latitude: "",
        longitude: "",
      },
      customer: {
        customerId: "",
        customerName: "",
        phone: "",
        whatsapp: "",
        email: "",
      },
    },
    priority: "medium",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        eventDetails: {
          eventName: initialData.eventDetails?.eventName || "",
          eventType: initialData.eventDetails?.eventType || "wedding",
          description: initialData.eventDetails?.description || "",
          bookingDate: initialData.eventDetails?.bookingDate || "",
          location: {
            venueName: initialData.eventDetails?.location?.venueName || "",
            address: initialData.eventDetails?.location?.address || "",
            area: initialData.eventDetails?.location?.area || "",
            city: initialData.eventDetails?.location?.city || "",
            district: initialData.eventDetails?.location?.district || "",
            state: initialData.eventDetails?.location?.state || "",
            pincode: initialData.eventDetails?.location?.pincode || "",
            latitude: initialData.eventDetails?.location?.latitude || "",
            longitude: initialData.eventDetails?.location?.longitude || "",
          },
          customer: {
            customerId: initialData.eventDetails?.customer?.customerId || "",
            customerName:
              initialData.eventDetails?.customer?.customerName || "",
            phone: initialData.eventDetails?.customer?.phone || "",
            whatsapp: initialData.eventDetails?.customer?.whatsapp || "",
            email: initialData.eventDetails?.customer?.email || "",
          },
        },
        priority: initialData.priority || "medium",
      });
    }
  }, [initialData]);

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split(".");
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
    if (errors[path]) setErrors((prev) => ({ ...prev, [path]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.eventDetails.eventName.trim())
      newErrors["eventDetails.eventName"] = "Event name is required";
    if (!formData.eventDetails.eventType)
      newErrors["eventDetails.eventType"] = "Event type is required";
    if (!formData.eventDetails.bookingDate)
      newErrors["eventDetails.bookingDate"] = "Booking date is required";
    if (!formData.eventDetails.customer.customerName.trim())
      newErrors["eventDetails.customer.customerName"] =
        "Customer name is required";
    if (!formData.eventDetails.customer.phone.trim())
      newErrors["eventDetails.customer.phone"] = "Phone is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({ ...formData, createdBy: user?.email || "admin@example.com" });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Event Name *"
          value={formData.eventDetails.eventName}
          onChange={(e) =>
            handleChange("eventDetails.eventName", e.target.value)
          }
          error={!!errors["eventDetails.eventName"]}
          helperText={errors["eventDetails.eventName"]}
          fullWidth
          disabled={loading}
          placeholder="e.g., Akash & Priya Wedding"
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors["eventDetails.eventType"]}>
              <InputLabel>Event Type *</InputLabel>
              <Select
                value={formData.eventDetails.eventType}
                onChange={(e) =>
                  handleChange("eventDetails.eventType", e.target.value)
                }
                label="Event Type *"
                disabled={loading}
              >
                {EVENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Booking Date *"
              type="date"
              value={formData.eventDetails.bookingDate}
              onChange={(e) =>
                handleChange("eventDetails.bookingDate", e.target.value)
              }
              error={!!errors["eventDetails.bookingDate"]}
              helperText={errors["eventDetails.bookingDate"]}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          label="Description"
          multiline
          rows={2}
          fullWidth
          disabled={loading}
          value={formData.eventDetails.description}
          onChange={(e) =>
            handleChange("eventDetails.description", e.target.value)
          }
        />

        <FormControl fullWidth>
          <InputLabel>Priority</InputLabel>
          <Select
            value={formData.priority}
            onChange={(e) => handleChange("priority", e.target.value)}
            label="Priority"
            disabled={loading}
          >
            {Object.values(EVENT_PRIORITY).map((p) => (
              <MenuItem key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider>
          <Typography variant="caption">Venue Details</Typography>
        </Divider>

        <TextField
          label="Venue Name"
          fullWidth
          disabled={loading}
          value={formData.eventDetails.location.venueName}
          onChange={(e) =>
            handleChange("eventDetails.location.venueName", e.target.value)
          }
        />
        <TextField
          label="Address"
          fullWidth
          disabled={loading}
          value={formData.eventDetails.location.address}
          onChange={(e) =>
            handleChange("eventDetails.location.address", e.target.value)
          }
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.location.city}
              onChange={(e) =>
                handleChange("eventDetails.location.city", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="District"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.location.district}
              onChange={(e) =>
                handleChange("eventDetails.location.district", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Pincode"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.location.pincode}
              onChange={(e) =>
                handleChange("eventDetails.location.pincode", e.target.value)
              }
            />
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Customer Details</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Customer Name *"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.customer.customerName}
              onChange={(e) =>
                handleChange(
                  "eventDetails.customer.customerName",
                  e.target.value,
                )
              }
              error={!!errors["eventDetails.customer.customerName"]}
              helperText={errors["eventDetails.customer.customerName"]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone *"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.customer.phone}
              onChange={(e) =>
                handleChange("eventDetails.customer.phone", e.target.value)
              }
              error={!!errors["eventDetails.customer.phone"]}
              helperText={errors["eventDetails.customer.phone"]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="WhatsApp"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.customer.whatsapp}
              onChange={(e) =>
                handleChange("eventDetails.customer.whatsapp", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              fullWidth
              disabled={loading}
              value={formData.eventDetails.customer.email}
              onChange={(e) =>
                handleChange("eventDetails.customer.email", e.target.value)
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Event"
                : "Create Event"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default EventForm;
